import { useAuth, useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  GraduationCap,
  ArrowLeft,
  Sun,
  Moon,
  Settings as SettingsIcon,
  LogOut,
  CreditCard,
  User,
  Palette,
  Bell,
  Calendar,
  Target,
} from "lucide-react";
import { motion } from "framer-motion";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { useState } from "react";
import { Loader2, Mail } from "lucide-react";

const Settings = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sendingTest, setSendingTest] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSendTestReminder = async () => {
    setSendingTest(true);
    try {
      const res = await apiClient.post("/send-test-reminder");
      toast.success(res.data.message || "Test reminder sent! Check your inbox.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send test email";
      toast.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
                data-testid="back-btn"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-8 h-8 text-primary" />
                <span className="font-bold text-xl font-outfit hidden sm:block">Admit List</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-8">
            Settings
          </h1>

          {/* Profile Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-outfit">
                <User className="w-5 h-5" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-xl">{user?.name?.[0] || "U"}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{user?.name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Signed in with Google
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-outfit">
                <Palette className="w-5 h-5" />
                Appearance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="dark-mode">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Toggle between light and dark theme
                  </p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={theme === "dark"}
                  onCheckedChange={toggleTheme}
                  data-testid="dark-mode-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Notifications Section (Placeholder) */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-outfit">
                <Bell className="w-5 h-5" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-reminders">Email Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Get reminded about upcoming deadlines
                  </p>
                </div>
                <Switch
                  id="email-reminders"
                  defaultChecked
                  data-testid="email-reminders-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="weekly-digest">Weekly Digest</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive a weekly summary of your applications
                  </p>
                </div>
                <Switch
                  id="weekly-digest"
                  data-testid="weekly-digest-switch"
                />
              </div>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Test Email</Label>
                    <p className="text-sm text-muted-foreground">
                      Send a test reminder to {user?.email || "your email"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendTestReminder}
                    disabled={sendingTest}
                    data-testid="send-test-reminder-btn"
                  >
                    {sendingTest ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {sendingTest ? "Sending..." : "Send Test"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-outfit">
                <CreditCard className="w-5 h-5" />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    {user?.subscription_status === "active" ? "Active Plan" : "Free Plan"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.subscription_status === "active"
                      ? `Expires ${new Date(user.subscription_end_date).toLocaleDateString()}`
                      : "Upgrade to unlock all features"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate("/subscription")}
                  data-testid="manage-subscription-btn"
                >
                  {user?.subscription_status === "active" ? "Manage" : "Upgrade"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive font-outfit">Danger Zone</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sign Out</p>
                  <p className="text-sm text-muted-foreground">
                    Sign out of your account on this device
                  </p>
                </div>
                <Button
                  variant="destructive"
                  className="rounded-full"
                  onClick={handleLogout}
                  data-testid="signout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2 text-primary"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <SettingsIcon className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default Settings;
